import React, { useCallback, useEffect, useRef, useState } from 'react';
import { IoMdArrowDropdown, IoMdArrowDropup } from 'react-icons/io';
import { getSortOptions } from '../../utils/attendanceTableSort';

export default function ColumnSortHeader({
  label,
  sortKey,
  sortType = 'text',
  activeSortBy,
  activeSortOrder,
  onSortChange,
  variant = 'dark',
}) {
  const [open, setOpen] = useState(false);
  const containerRef = useRef(null);
  const isActive = activeSortBy === sortKey;
  const options = getSortOptions(sortType);

  const closeMenu = useCallback(() => setOpen(false), []);

  useEffect(() => {
    if (!open) return undefined;

    const handlePointerDown = (event) => {
      if (containerRef.current && !containerRef.current.contains(event.target)) {
        closeMenu();
      }
    };

    const handleKeyDown = (event) => {
      if (event.key === 'Escape') {
        closeMenu();
      }
    };

    document.addEventListener('mousedown', handlePointerDown);
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('mousedown', handlePointerDown);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [open, closeMenu]);

  const handleToggle = (event) => {
    event.stopPropagation();
    setOpen((prev) => !prev);
  };

  const handleSelect = (order) => {
    if (isActive && activeSortOrder === order) {
      onSortChange('', 'asc');
    } else {
      onSortChange(sortKey, order);
    }
    closeMenu();
  };

  const headerTextClass = variant === 'dark' ? 'text-white' : 'text-gray-900';
  const chevronClass = variant === 'dark' ? 'text-white' : 'text-gray-700';

  return (
    <div ref={containerRef} className="relative">
      <button
        type="button"
        onClick={handleToggle}
        className={`flex items-center gap-1 whitespace-nowrap font-semibold ${headerTextClass}`}
        aria-haspopup="menu"
        aria-expanded={open}
      >
        <span>{label}</span>
        {isActive && activeSortOrder === 'asc' && (
          <IoMdArrowDropup className={`text-base shrink-0 ${chevronClass}`} aria-hidden />
        )}
        {isActive && activeSortOrder === 'desc' && (
          <IoMdArrowDropdown className={`text-base shrink-0 ${chevronClass}`} aria-hidden />
        )}
        <IoMdArrowDropdown className={`text-base shrink-0 ${chevronClass}`} aria-hidden />
      </button>

      {open && (
        <div
          role="menu"
          className="absolute left-0 top-full z-30 mt-1 min-w-[11rem] rounded-md border border-gray-200 bg-white py-1 shadow-lg"
        >
          {options.map((option) => {
            const selected = isActive && activeSortOrder === option.order;
            return (
              <button
                key={option.order}
                type="button"
                role="menuitem"
                onClick={() => handleSelect(option.order)}
                className={`flex w-full items-center gap-2 px-3 py-2 text-left text-sm text-gray-800 hover:bg-gray-100 ${
                  selected ? 'bg-gray-50' : ''
                }`}
              >
                <span className="w-4 shrink-0 text-gray-600">
                  {selected ? '✓' : ''}
                </span>
                {option.order === 'asc' ? (
                  <IoMdArrowDropup className="text-base shrink-0 text-gray-600" aria-hidden />
                ) : (
                  <IoMdArrowDropdown className="text-base shrink-0 text-gray-600" aria-hidden />
                )}
                <span>{option.label}</span>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}
